import { useMutation, useQuery } from "@apollo/client";
import {
  Button as DefaultButton,
  Card,
  InputField,
  Middle,
  pxToRem,
  Spacing,
  Text,
} from "@ottatech/design";
import {
  composeValidators,
  minLength,
  required,
  validEmail,
  validLinkedinProfileUrl,
} from "@ottatech/shared-components";
import { FieldValidator } from "final-form";
import React, { useCallback, useEffect } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { Link as RouterLink } from "react-router-dom";
import styled from "styled-components";
import { pushAnalyticsEvent } from "@ottatech/analytics";

import { CookiePolicy } from "./CookiePolicy";
import { EmailNotifications } from "./EmailNotifications";
import { UnhideCompany } from "./UnhideCompany";
import { LocationSettings } from "./LocationSettings";

import { Link } from "components/Link";
import { PageWrapper } from "components/PageWrapper";
import {
  AccountSettings,
  AccountSettingsDocument,
  UpdateCurrentUserDocument,
} from "schema";

const Button = styled(DefaultButton)`
  width: 100%;
`;

type SettingsInputType = "email" | "text";

type SettingsName = "email" | "firstName" | "lastName" | "linkedinUrl";

interface SettingsFieldProps {
  name: SettingsName;
  type: SettingsInputType;
  label: string;
  validators: FieldValidator<string>;
  onBlur(name: SettingsName, value: string | null): void;
  placeholder?: string;
}

function SettingsField({
  name,
  type,
  label,
  validators,
  onBlur,
  placeholder,
}: SettingsFieldProps) {
  return (
    <Field<string> name={name} validate={validators}>
      {({ input, meta }) => {
        return (
          <InputField
            {...input}
            placeholder={placeholder}
            type={type}
            name={name}
            label={label}
            error={(meta.active || meta.dirty) && meta.error}
            onBlur={e => {
              onBlur(name, e.target.value || null);
            }}
          />
        );
      }}
    </Field>
  );
}

function FormFields(): React.ReactElement {
  const [updateMutation] = useMutation(UpdateCurrentUserDocument);

  const handleBlur = useCallback(
    (name: SettingsName, value: string | null) => {
      updateMutation({ variables: { input: { [name]: value } } });
    },
    [updateMutation]
  );

  return (
    <Spacing size={-1}>
      <SettingsField
        name="email"
        label="Email"
        type="email"
        validators={composeValidators(required, minLength(5), validEmail)}
        onBlur={handleBlur}
      />
      <SettingsField
        name="firstName"
        type="text"
        label="First name"
        validators={composeValidators(required, minLength(2))}
        onBlur={handleBlur}
      />
      <SettingsField
        name="lastName"
        type="text"
        label="Last name"
        validators={composeValidators(required, minLength(2))}
        onBlur={handleBlur}
      />
      <SettingsField
        name="linkedinUrl"
        type="text"
        label="LinkedIn URL"
        validators={validLinkedinProfileUrl}
        placeholder="https://linkedin.com/in/your-name"
        onBlur={handleBlur}
      />
    </Spacing>
  );
}

const SettingsForm = ({
  currentUser,
}: {
  currentUser: AccountSettings.CurrentUser;
}) => (
  <Card>
    <Spacing>
      <Text bold size={1}>
        Settings
      </Text>
      <FinalForm
        initialValues={{
          email: currentUser.email,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          linkedinUrl: currentUser.linkedinUrl,
        }}
        onSubmit={() => undefined}
        render={() => (
          <form>
            <Spacing size={2}>
              <FormFields />
              <Spacing>
                <Button
                  as={RouterLink}
                  level="secondary"
                  to="/logout"
                  data-analytics-id="logout"
                >
                  Log out
                </Button>
                <Link
                  to="/delete-my-account"
                  data-analytics-id="delete-my-account"
                >
                  Delete my account
                </Link>
              </Spacing>
            </Spacing>
          </form>
        )}
      />
    </Spacing>
  </Card>
);

export function Settings(): React.ReactElement | null {
  const { data } = useQuery(AccountSettingsDocument);

  useEffect(() => {
    pushAnalyticsEvent({
      eventName: "Candidate Viewed",
      pathname: location.pathname,
    });
  }, []);

  if (!data || data.currentUser === null) {
    return null;
  }

  return (
    <PageWrapper style={{ marginTop: pxToRem(20) }}>
      <Middle maxWidth={390}>
        <Spacing>
          <Text bold size={3} align="center">
            Your account
          </Text>
          <SettingsForm currentUser={data.currentUser} />
          <LocationSettings currentUser={data.currentUser} />
          <EmailNotifications />
          <UnhideCompany currentUser={data.currentUser} />
          <CookiePolicy />
        </Spacing>
      </Middle>
    </PageWrapper>
  );
}
